/**
 * 安全的 localStorage 包装器
 * 解决服务端渲染和隐私模式下 localStorage 不可用的问题
 */

type StorageType = 'localStorage' | 'sessionStorage';

const checkStorageAvailable = (storageType: StorageType): boolean => {
  try {
    const storage = window[storageType];
    return !!storage;
  } catch {
    return false;
  }
};

/**
 * 检查 localStorage 是否可用
 * 主要解决 "Access is denied for this document" 错误
 */
export const checkLocalStorageAvailable = (): boolean => checkStorageAvailable('localStorage');

/**
 * 检查 sessionStorage 是否可用
 * 主要解决 SSR、隐私模式或浏览器限制下 sessionStorage 不可用的问题
 */
export const checkSessionStorageAvailable = (): boolean => checkStorageAvailable('sessionStorage');

class SafeStorage {
  private readonly storageType: StorageType;
  private readonly availabilityChecker: () => boolean;
  private _isAvailable: boolean | null = null;

  constructor(storageType: StorageType, availabilityChecker: () => boolean) {
    this.storageType = storageType;
    this.availabilityChecker = availabilityChecker;
  }

  private checkAvailability(): boolean {
    if (this._isAvailable === true) {
      return true;
    }

    const isAvailable = this.availabilityChecker();
    this._isAvailable = isAvailable;
    return isAvailable;
  }

  private getStorage(): Storage {
    return window[this.storageType];
  }

  private resetAvailabilityCache(): void {
    this._isAvailable = null;
  }

  setItem(key: string, value: string): boolean {
    if (!this.available) {
      return false;
    }

    try {
      this.getStorage().setItem(key, value);
      return true;
    } catch {
      this.resetAvailabilityCache();
      return false;
    }
  }

  getItem(key: string): string | null {
    if (!this.available) {
      return null;
    }

    try {
      return this.getStorage().getItem(key);
    } catch {
      this.resetAvailabilityCache();
      return null;
    }
  }

  removeItem(key: string): boolean {
    if (!this.available) {
      return false;
    }

    try {
      this.getStorage().removeItem(key);
      return true;
    } catch {
      this.resetAvailabilityCache();
      return false;
    }
  }

  clear(): boolean {
    if (!this.available) {
      return false;
    }

    try {
      this.getStorage().clear();
      return true;
    } catch {
      this.resetAvailabilityCache();
      return false;
    }
  }

  get length(): number {
    if (!this.available) {
      return 0;
    }

    try {
      return this.getStorage().length;
    } catch {
      this.resetAvailabilityCache();
      return 0;
    }
  }

  key(index: number): string | null {
    if (!this.available) {
      return null;
    }

    try {
      return this.getStorage().key(index);
    } catch {
      this.resetAvailabilityCache();
      return null;
    }
  }

  get available(): boolean {
    return this.checkAvailability();
  }

  recheck(): boolean {
    this.resetAvailabilityCache();
    return this.checkAvailability();
  }
}

export const safeStorage = new SafeStorage('localStorage', checkLocalStorageAvailable);
export const safeSessionStorage = new SafeStorage('sessionStorage', checkSessionStorageAvailable);
